import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CustomPagination from '../../../components/forms/Pagination';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, IconButton, Link, SvgIcon, TableSortLabel, Tooltip, Typography, useTheme } from '@mui/material';
import { GetPurchaseOrderHeadersResponse } from '../../../app/services/api/generated';
import { useNavigate } from 'react-router-dom';
import { getFormattedDate } from './../../../utils';
import { Lock01, CreditCard } from './../../../assets';
import InfoIcon from '@mui/icons-material/Info';
import { ConfigurationName } from '../../../enums/ConfigurationName';
import { useConfigurations } from '../../../components/shared/useConfigurations';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import { TableLoading } from '../../../components/forms/TableLoading';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import { isReturnToVendorButtonVisibleForFranchise } from '../../../app/services/environment';
import { UserContext } from '../../../components/shared/useUser';

export default function PurchaseOrdersTable(props: {
  readonly list: GetPurchaseOrderHeadersResponse,
  readonly setPageZero: number;
  readonly loadData: (sort: string, direction: string, skip: number, take: number) => void;
  order: 'desc' | 'asc';
  orderBy: string;
  loading: boolean;
  handleSort: (property: string) => void;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  search: string;
  clearAll: () => void;
  canCreateAdHocReceipt: boolean;
  onCreateAdHocReceipt: () => void;
}) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const theme = useTheme();

  const maxPages = props.list?.purchaseOrderHeadersCount
    ? Math.max(1, Math.ceil(props.list.purchaseOrderHeadersCount / 10))
    : 0;
  //Header sort
  const { isConfigurationEnabled } = useConfigurations();
  const { selectedStore } = React.useContext(UserContext);
  const isReturnToVendorEnabled = isConfigurationEnabled(ConfigurationName.ReturnToVendorEnabled);
  const isCreditRequestEnabled = isConfigurationEnabled(ConfigurationName.CreditRequestsEnabled);

  const getCRStatusText = (status?: number) => {
    const actualStatus = status ?? 0;
    switch (actualStatus) {
      case 0:
        return t('creditRequest.status.notCreated');
      case 1:
        return t('creditRequest.status.createdNotSent');
      case 2:
        return t('creditRequest.status.sent');
      default:
        return '';
    }
  };

  const getRtVStatusText = (status?: number) => {
    const actualStatus = status ?? 0;
    switch (actualStatus) {
      case 0:
        return t('returnToVendor.status.notCreated');
      case 1:
        return t('returnToVendor.status.createdNotSent');
      case 2:
        return t('returnToVendor.status.sent');
      default:
        return '';
    }
  };

  if (props.list.purchaseOrderHeadersCount == 0 && !props.loading) {
    return <EmptyTable bodyRowHeight={40}
      headerHeight={45}
      text={t('purchaseOrderReceipt.title')}
      searchTerm={props.search}
      onClear={props.clearAll}
      onAdd={() => { }}
      onAddIsCreating={false}
      addItemText={''}
      showOnAdd={false}
      onAddIsDisabled={false} />;
  }

  return (
    <>
      <TableHeader>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {t('purchaseOrderReceipt.title')}
            {(props.list.purchaseOrderHeadersCount ?? 0) > 0 && <Pill text={`${props.list.purchaseOrderHeadersCount} ${t('purchaseOrderReceipt.title')}`}></Pill>}
          </Box>
          {props.canCreateAdHocReceipt && <Button
            variant="primary"
            size="md"
            onClick={props.onCreateAdHocReceipt}>
            {t('purchaseOrderReceipt.adHocReceiptModal.openButton')}
          </Button>}
        </Box>
      </TableHeader>
      {props.loading ? <TableLoading headerHeight={45}
        bodyRowHeight={40}
        minTableWidth={1120} /> :
        <TableContainer>
          <Table sx={{ minWidth: '1120px' }}>
            <TableHead>
              <TableRow>
                <TableCell align="left">
                </TableCell>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={props.orderBy === 'ewsOrderNumber'}
                    direction={props.order}
                    onClick={() => props.handleSort('ewsOrderNumber')}
                  >
                    {t('purchaseOrderReceipt.purchaseOrder')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={props.orderBy === 'orderNumber'}
                    direction={props.order}
                    onClick={() => props.handleSort('orderNumber')}
                  >
                    {t('purchaseOrderReceipt.orderReference')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={props.orderBy === 'acceptedDate'}
                    direction={props.order}
                    onClick={() => props.handleSort('acceptedDate')}
                  >
                    {t('purchaseOrderReceipt.receiptDate')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={props.orderBy === 'deliveryDate'}
                    direction={props.order}
                    onClick={() => props.handleSort('deliveryDate')}
                  >
                    {t('purchaseOrderReceipt.deliveryDate')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap' }}>
                  {t('purchaseOrderReceipt.receivedBy')}
                </TableCell>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={props.orderBy === 'orderType'}
                    direction={props.order}
                    onClick={() => props.handleSort('orderType')}
                  >
                    {t('purchaseOrderReceipt.orderType')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left">
                  <TableSortLabel
                    active={props.orderBy === 'supplierName'}
                    direction={props.order}
                    onClick={() => props.handleSort('supplierName')}
                  >
                    {t('purchaseOrderReceipt.supplierName')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap' }}>
                  {t('purchaseOrderReceipt.purchaseOrderStatus')}
                </TableCell>
                {isCreditRequestEnabled && <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap' }}>
                  {t('purchaseOrderReceipt.creditRequestStatus')}
                </TableCell>}
                {isReturnToVendorEnabled && isReturnToVendorButtonVisibleForFranchise(selectedStore?.franchiseName ?? '') && <TableCell align="left">
                  {t('purchaseOrderReceipt.rtvStatus')}
                </TableCell>}
                <TableCell align="left"
                  sx={{ whiteSpace: 'nowrap' }}>
                  {t('purchaseOrderReceipt.note')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.list?.purchaseOrderHeaders?.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/create-goods-receipt/${row.id}`)}
                >
                  <TableCell sx={{ py: 0, minWidth: '35px', maxWidth: '35px' }}>
                    {row.creditRequestStatus !== 0 ? (
                      <SvgIcon sx={{ height: '24px', width: '24px', py: 0 }}>
                        <CreditCard />
                      </SvgIcon>
                    ) : (
                      <>
                      </>

                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Grid container
                      alignItems="center"
                      spacing={1}>
                      <Grid item>
                        <Typography variant='textXS'
                          fontWeight={600}
                          color={theme.palette.primary[500]}
                          sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                          {row.ewsOrderNumber}
                        </Typography>
                      </Grid>
                      <Grid item>
                        {row.externallyCreatedOrder && (
                          <Tooltip title="Order created outside of Orderly"
                            arrow>
                            <InfoIcon
                              fontSize="small"
                              color="primary"
                              sx={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        )}
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.orderNumber}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.acceptedDate ? getFormattedDate(new Date(row.acceptedDate)) : null}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Grid container
                      alignItems="center"
                      spacing={1}>
                      <Grid item>
                        {row.deliveryDate ? getFormattedDate(new Date(row.deliveryDate)) : null}
                      </Grid>
                      <Grid item>
                        {row.deliveryDateUpdated && (
                          <Tooltip title="Updated"
                            arrow>
                            <InfoIcon
                              fontSize="small"
                              color="primary"
                              sx={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        )}
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.receivedBy}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.orderType}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.supplierName}
                  </TableCell>
                  <TableCell sx={{ py: 0 }}>
                    {row.isReceived ? <SvgIcon sx={{ height: '24px', width: '24px', py: 0 }}>
                      <Lock01 color='gray'
                        width='80%' />
                    </SvgIcon> : ''}
                  </TableCell>
                  {isCreditRequestEnabled && <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.creditRequestStatus !== 0 ? (<Link href={`/store/credit-request/${row.id}`}
                      onClick={(event) => {
                        navigate(`/store/credit-request/${row.id}`);
                        event.stopPropagation();
                        event.preventDefault();
                      }}
                      color="primary"
                      underline={'always'}
                      style={{ cursor: 'pointer' }}
                    >
                      {getCRStatusText(row.creditRequestStatus)}
                    </Link>) : (
                      <span>{getCRStatusText(row.creditRequestStatus)}</span>)}
                  </TableCell>}
                  {isReturnToVendorEnabled && isReturnToVendorButtonVisibleForFranchise(selectedStore?.franchiseName ?? '') && <TableCell>
                    {row.returnToVendorStatus !== 0 ? (
                      <Link href={`/store/credit-request/${row.id}`}
                        onClick={(event) => {
                          navigate(`/store/credit-request/${row.id}`);
                          event.stopPropagation();
                          event.preventDefault();
                        }}
                        color="primary"
                        underline={'always'}
                        style={{ cursor: 'pointer' }}
                      >
                        {getRtVStatusText(row.returnToVendorStatus)}
                      </Link>) : (
                      <span>{getRtVStatusText(row.returnToVendorStatus)}</span>)}
                  </TableCell>
                  }
                  <TableCell sx={{ py: 0 }}>
                    <Tooltip title={row.note}>
                      <IconButton aria-label={row.note}
                        disabled={!row.note}
                        sx={{ height: '24px', width: '24px', p: 0 }}>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              <EmptyRows numberOfCurrentRows={props.list?.purchaseOrderHeaders?.length ?? 0}
                height={40} />
            </TableBody>
          </Table>
        </TableContainer>}
      <CustomPagination useNoDataMessage={false}
        page={props.page}
        setPage={props.setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
      />
    </>
  );
}
